'use client'

import { useRouter } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import Image from 'next/image'
import React, { useRef, useState } from 'react'

export type SwipeSibling = {
  slug: string
  title: string
  thumbnailUrl: string | null
} | null

type Props = {
  prevProduct: SwipeSibling
  nextProduct: SwipeSibling
  locale: string
  children: React.ReactNode
}

const SWIPE_THRESHOLD_PX = 70
const MAX_DRAG_PX = 120

export function ProductSwipeNav({ prevProduct, nextProduct, locale, children }: Props) {
  const router = useRouter()
  const t = useTranslations('product')
  const rootRef = useRef<HTMLDivElement>(null)
  const [dragX, setDragX] = useState(0)
  const [dragging, setDragging] = useState(false)

  const gesture = useRef<{
    startX: number
    startY: number
    axis: 'x' | 'y' | null
    pointerId: number
  } | null>(null)

  function onPointerDown(e: React.PointerEvent) {
    if (e.pointerType !== 'touch') return
    const target = e.target as HTMLElement
    // Let the image gallery own its own horizontal swipe (image carousel) —
    // product-to-product swipe only engages outside that zone.
    if (target.closest('[data-swipe-ignore]')) return

    gesture.current = { startX: e.clientX, startY: e.clientY, axis: null, pointerId: e.pointerId }
  }

  function onPointerMove(e: React.PointerEvent) {
    const g = gesture.current
    if (!g || e.pointerId !== g.pointerId) return

    const dx = e.clientX - g.startX
    const dy = e.clientY - g.startY

    if (g.axis === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      if (g.axis === 'x') setDragging(true)
    }

    if (g.axis !== 'x') return

    // Rubber-band resistance, and refuse to drag past an edge with no sibling.
    const blocked = (dx < 0 && !nextProduct) || (dx > 0 && !prevProduct)
    const resisted = blocked ? dx / 4 : dx
    const clamped = Math.max(-MAX_DRAG_PX, Math.min(MAX_DRAG_PX, resisted))
    setDragX(clamped)
  }

  function endGesture(e: React.PointerEvent) {
    const g = gesture.current
    if (!g || e.pointerId !== g.pointerId) {
      gesture.current = null
      return
    }

    if (g.axis === 'x') {
      if (dragX <= -SWIPE_THRESHOLD_PX && nextProduct) {
        router.push(`/products/${nextProduct.slug}`)
      } else if (dragX >= SWIPE_THRESHOLD_PX && prevProduct) {
        router.push(`/products/${prevProduct.slug}`)
      }
    }

    gesture.current = null
    setDragging(false)
    setDragX(0)
  }

  return (
    <div>
      {(prevProduct || nextProduct) && (
        <div className="mb-4 flex items-stretch gap-2 sm:hidden">
          <SwipePeekCard product={prevProduct} direction="prev" locale={locale} label={t('previousProduct')} />
          <SwipePeekCard product={nextProduct} direction="next" locale={locale} label={t('nextProduct')} />
        </div>
      )}

      <div
        ref={rootRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        style={{
          touchAction: 'pan-y',
          transform: dragX ? `translateX(${dragX}px)` : undefined,
          transition: dragging ? 'none' : 'transform 200ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  )
}

function SwipePeekCard({
  product,
  direction,
  locale,
  label,
}: {
  product: SwipeSibling
  direction: 'prev' | 'next'
  locale: string
  label: string
}) {
  if (!product) {
    return <div className="flex-1" aria-hidden="true" />
  }

  return (
    <Link
      href={`/${locale}/products/${product.slug}`}
      className={`flex flex-1 items-center gap-2 overflow-hidden rounded-xl border border-white/10 bg-white/5 px-2.5 py-2 text-left active:bg-white/10 ${
        direction === 'next' ? 'flex-row-reverse text-right' : ''
      }`}
    >
      {product.thumbnailUrl ? (
        <Image
          src={product.thumbnailUrl}
          alt=""
          width={36}
          height={36}
          className="h-9 w-9 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="h-9 w-9 shrink-0 rounded-lg bg-white/10" />
      )}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-white/50">
          {direction === 'prev' ? `← ${label}` : `${label} →`}
        </div>
        <div className="truncate text-xs font-medium text-white/90">{product.title}</div>
      </div>
    </Link>
  )
}
